import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ListChecks, Archive, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { TYPE_LABEL } from "@/lib/constants";
import { ContentDrawer } from "@/components/ContentDrawer";

function TaskItem({ it, onSelect, archived = false }) {
  return (
    <button
      data-testid={`task-item-${it.id}`}
      onClick={() => onSelect(it)}
      className={`w-full text-left flex items-center gap-2 p-2.5 rounded-lg border transition ${
        archived ? "border-neutral-100 bg-neutral-50/60 opacity-70 hover:opacity-100 hover:bg-neutral-50" : "border-neutral-100 hover:bg-neutral-50"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-neutral-900 truncate">{it.title}</div>
        <div className="text-[11px] text-neutral-400">{TYPE_LABEL[it.content_type]}</div>
      </div>
      <StatusBadge status={it.status} />
    </button>
  );
}

export default function CreatorTasks() {
  const { accountKey, refreshApprovals } = useOutletContext();
  const [data, setData] = useState({ creators: [] });
  const [selected, setSelected] = useState(null);
  const [openArchives, setOpenArchives] = useState({});

  const load = () => api.get("/tasks", { params: { account: accountKey } }).then(({ data }) => setData(data));
  useEffect(() => { if (accountKey) load(); }, [accountKey]);

  const toggleArchive = (name) => setOpenArchives((s) => ({ ...s, [name]: !s[name] }));

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto animate-rise">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
        <ListChecks className="h-3.5 w-3.5" /> Creator-Aufgaben
      </p>
      <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight mt-1">Wer arbeitet woran?</h1>
      <p className="text-sm text-neutral-400 mt-1">Pro Creator sind die 5 aktuellsten Aufgaben sichtbar — ältere liegen im Archiv.</p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.creators.map((cr) => {
          const archive = cr.archive || [];
          const archiveOpen = !!openArchives[cr.name];
          return (
            <div key={cr.name} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ background: cr.avatar_color }}>
                  {cr.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </span>
                <div className="flex-1">
                  <div className="font-head font-semibold text-neutral-900">{cr.name}</div>
                  <div className="text-xs text-neutral-400">{cr.open} offen · {cr.done} erledigt</div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {cr.items.length === 0 && <p className="text-sm text-neutral-400">Keine Aufgaben.</p>}
                {cr.items.map((it) => (
                  <TaskItem key={it.id} it={it} onSelect={setSelected} />
                ))}
              </div>
              {archive.length > 0 && (
                <div className="mt-3">
                  <button
                    data-testid={`task-archive-toggle-${cr.name.replaceAll(" ", "-")}`}
                    onClick={() => toggleArchive(cr.name)}
                    className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold text-neutral-500 hover:text-neutral-800 py-2 rounded-lg border border-dashed border-neutral-200 hover:bg-neutral-50 transition"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    Archiv ({archive.length})
                    {archiveOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  {archiveOpen && (
                    <div className="mt-2 space-y-2" data-testid={`task-archive-list-${cr.name.replaceAll(" ", "-")}`}>
                      {archive.map((it) => (
                        <TaskItem key={it.id} it={it} onSelect={setSelected} archived />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {data.creators.length === 0 && <div className="col-span-full text-center text-neutral-400 py-16">Keine Creator zugeordnet.</div>}
      </div>

      <ContentDrawer item={selected} onClose={() => setSelected(null)} onChanged={() => { load(); refreshApprovals?.(); }} />
    </div>
  );
}
